/**
 * Vue 3 composable for managing SEO meta tags
 *
 * This composable uses @vueuse/head to reactively update SEO meta tags.
 */

import { useRoute } from "vue-router";
import { useHead } from "@vueuse/head";
import { getCanonicalUrl } from "@services/SeoService.js";

/**
 * SEO composable for Vue components
 *
 * @param {Object} options - SEO options object
 * @param {string|Function} options.title - Page title or function returning title
 * @param {string|Function} options.description - Page description or function returning description
 * @param {string|Function} options.canonicalUrl - Custom canonical URL or function (optional)
 * @param {string|Function} options.locale - Page locale or function (optional, defaults to 'en-US')
 * @param {Object|Function} options.structuredData - JSON-LD structured data object or function (optional)
 * @param {string|Function} options.robots - Robots meta directive or function (optional)
 * @param {string|Function} options.ogImage - Open Graph image URL or function (optional)
 * @param {string|Function} options.ogType - Open Graph type or function (optional)
 */
export function useSeo(options) {
  const route = useRoute();

  // Helper to resolve values that might be functions
  const resolveValue = (value) => {
    return typeof value === "function" ? value() : value;
  };

  useHead(() => {
    const title = resolveValue(options.title);
    const description = resolveValue(options.description);
    const locale = resolveValue(options.locale) || route.params.lang || "en-US";
    const robots = resolveValue(options.robots);
    const ogImage = resolveValue(options.ogImage);
    const ogType = resolveValue(options.ogType) || "website";

    const canonicalUrl = options.canonicalUrl
      ? resolveValue(options.canonicalUrl)
      : getCanonicalUrl(route.path, locale);

    const rawStructuredData = options.structuredData
      ? resolveValue(options.structuredData)
      : {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: title,
          description: description,
          url: canonicalUrl,
        };

    // Support single object or array of structured data objects
    const structuredDataArray = Array.isArray(rawStructuredData)
      ? rawStructuredData
      : [rawStructuredData];

    // Strip locale prefix from path for hreflang hrefs
    const localeRegex = /^\/([a-z]{2}(-[A-Z]{2})?)(\/|$)/;
    const cleanPath = localeRegex.test(route.path)
      ? route.path.replace(localeRegex, "/")
      : route.path;
    const baseUrl = "https://spinthenames.com";

    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Spin the Names",
      url: baseUrl,
      logo: `${baseUrl}/logo.png`,
      sameAs: [],
    };

    return {
      title,
      htmlAttrs: {
        lang: locale,
      },
      meta: [
        { name: "description", content: description },
        { name: "robots", content: robots },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: ogType },
        {
          property: "og:image",
          content:
            ogImage || "https://spinthenames.com/images/spinthenamesstatic.png",
        },
        { property: "og:site_name", content: "Spin the Names" },
      ].filter((m) => m.content),
      link: [
        { rel: "canonical", href: canonicalUrl },
        { rel: "alternate", hreflang: "en", href: `${baseUrl}${cleanPath}` },
        { rel: "alternate", hreflang: "es", href: `${baseUrl}/es${cleanPath}` },
        { rel: "alternate", hreflang: "fr", href: `${baseUrl}/fr${cleanPath}` },
        { rel: "alternate", hreflang: "de", href: `${baseUrl}/de${cleanPath}` },
        { rel: "alternate", hreflang: "sv", href: `${baseUrl}/sv${cleanPath}` },
        { rel: "alternate", hreflang: "x-default", href: `${baseUrl}${cleanPath}` },
      ],
      script: [
        ...structuredDataArray.map((sd) => ({
          type: "application/ld+json",
          children: JSON.stringify(sd),
        })),
        {
          type: "application/ld+json",
          children: JSON.stringify(organizationSchema),
        },
      ],
    };
  });

  // Return empty object as we no longer need manual update methods
  return {};
}
